/**
 * Zod schema and inferred type for the person-core brief output (Track A).
 * The schema serves three roles — LLM output parsing, domain type, and MongoDB persistence shape — unified into a single source of truth.
 * LLM-side leniency (fields the LLM is allowed to omit) is expressed via `.optional().default(...)`.
 * Per-field `.describe()` annotations flow through Zod v4's native `.toJSONSchema()` into the JSON Schema's `description` fields, which Anthropic's structured-output API feeds to the LLM. The schema is therefore the primary contract — user-prompt prose carries only cross-field rules, policy guidance, and quality checks that can't be expressed in a schema.
 * Value normalizations — source aliasing, confidence coercion, direction empty-string handling, the enforced `seekingTier`, and the derived `evidenceSources` — all live in post-processing (see steps/person-core-brief/post-processing.ts).
 * Backend data shape uses a single Zod schema as the source of truth; types are derived via z.output<> and z.input<>.
 */

import { z } from "zod";
import { ClaimSchema } from "./claim.schemas.js";

export const ReasonToMeetSchema = z.object({
  type: z
    .string()
    .describe(
      "One of: learn, partner, invest, hire, sell, mission, play, explore, reconnect, like. `learn`/`partner` are obvious; `explore` (someone in a totally different world that would be fascinating) and `like` (you just want to meet the human) are what make connections feel human, not algorithmic.",
    ),
  signal: z
    .string()
    .describe(
      "A SPECIFIC, narrow signal grounded in THIS person's experience — not 'has business experience' but 'scaled a food company through COVID supply chain chaos — 150M meals, knows every logistics nightmare.' For `explore`/`like`/`play`: name a specific matchable interest/hobby/background element, never a professional restatement.",
    ),
  direction: z
    .string()
    .optional()
    .default("both")
    .describe(
      "Relationship direction. Required on every reason. For transactional types (learn/partner/invest/hire/sell): `seeking` vs `offering` (e.g. 'invest: seeking capital' vs 'invest: deploying capital'). For non-transactional (play/explore/like/mission): `both`.",
    ),
  confidence: z
    .string()
    .optional()
    .default("medium")
    .describe(
      "One of: high, medium, low. Scale to evidence density: single mention → low (qualified, 'has shown interest in'); repeated signals → medium ('appears to be building expertise in'); core-to-role plus reinforcing signals → high ('deeply embedded in').",
    ),
  basis: z
    .string()
    .optional()
    .default("")
    .describe(
      "The specific evidence supporting this reason — career fact, LinkedIn post, stated goal, board seat, intake answer, etc.",
    ),
  source: z
    .string()
    .optional()
    .default("ai_inference")
    .describe(
      "Where the signal came from. One of: linkedin (profile/career/posts), self_report (intakeAnswers / dossier_* fields / Porter conversation), voice_call (voice-call transcripts), host_input (host notes or event context), registration (registration forms), ai_inference (inference from patterns), pattern.",
    ),
  // Nothing reads this, but it stays: the schema IS the structured-output contract
  // sent to the model, so dropping the field changes every brief it writes. Removing
  // it flipped a pinned S1 selection on the asymmetric-interest trap (PLA-1890).
  _factual: z.boolean().optional().default(false),
  seekingTier: z
    .string()
    .optional()
    .default("")
    .describe(
      "Required when direction is `seeking`; use empty string for `offering`/`both`. `priority` — the person DECLARED this need themselves (source self_report/voice_call/registration). `observed` — their BEHAVIOR shows it (source typically linkedin/host_input). `likely` — inferred from situation/chapter/stage/role (source ai_inference).",
    ),
  keywords: z
    .array(z.string())
    .optional()
    .default([])
    .describe(
      "3-5 matchable terms for downstream matching engines, e.g. ['tennis', 'padel', 'competitive-sports', 'Miami']. Include on at least one reason per person; use empty array otherwise.",
    ),
});

const LifeMissionSchema = z.object({
  cause: z
    .string()
    .describe(
      "Cause or mission beyond business — ONLY if there is DIRECT evidence (board seats at nonprofits, LinkedIn posts about the cause, company mission explicitly tied to it, volunteer roles listed). Do NOT manufacture causes from nationality, sector, or age. PARTICIPATION IS NOT MISSION: Endeavor/YC/Stanford GSB/teaching one course are PROVENANCE (route to networkHypotheses), not mission. A mission requires sustained, voluntary commitment BEYOND their primary job. The test: if this person quit their job tomorrow, would they still work on this cause?",
    ),
  role: z
    .string()
    .optional()
    .default("practitioner")
    .describe("One of: advocate, practitioner, funder, researcher, volunteer."),
  evidence: z
    .string()
    .optional()
    .default("")
    .describe(
      "The SPECIFIC evidence — name the board seat, the post, the career choice. If you can't point to something concrete, don't include this entry.",
    ),
  confidence: z
    .string()
    .optional()
    .default("medium")
    .describe("One of: high, medium, low."),
  intensity: z
    .string()
    .optional()
    .default("active")
    .describe("One of: core, active, exploratory."),
});

const CareerSignatureSchema = z.object({
  pattern: z
    .string()
    .optional()
    .default("")
    .describe(
      "2-3 sentences describing HOW this person moves through their career — not what they did, but the recurring instinct or approach across moves. Describe the pattern, don't name it. Use neutral explanatory language — never loaded words like 'mercenary', 'immigrant', 'extreme', 'obsessive'. BAD: 'Operational mercenary with sector-agnostic leverage'. GOOD: 'Moves between unrelated industries every few years, always in the same role: taking something that works and making it work at scale. The expertise isn't in any one domain — it's in the scaling playbook itself.'",
    ),
  transitions: z
    .array(z.string())
    .optional()
    .default([])
    .describe(
      "1-3 key career transitions and what they reveal. Not 'left X for Y' but 'left payments after 8 years when he realized he was optimizing someone else's vision — the move to climate wasn't a pivot, it was a correction'.",
    ),
  whatCarries: z
    .string()
    .optional()
    .default("")
    .describe(
      "The capability or instinct that transfers across ALL their moves — the through-line. 'Community building' or 'product sense for collaboration tools' or 'ability to sell a vision in markets where nobody believes yet'.",
    ),
  whatDoesnt: z
    .string()
    .optional()
    .default("")
    .describe(
      "What SPECIFICALLY doesn't transfer — not 'domain expertise resets' (too generic). Name the actual thing: 'knows how central banks think but not how to ship a mobile app' or 'investor credibility doesn't carry — founders trust operators, not ex-operators.' The more specific and slightly uncomfortable, the better.",
    ),
  currentChapter: z
    .string()
    .optional()
    .default("")
    .describe(
      "What makes THIS chapter different from the previous ones — is this a continuation, a culmination, or another fresh start?",
    ),
});

const TravelHypothesisSchema = z.object({
  hypothesis: z
    .string()
    .describe(
      "Travel corridor/pattern hypothesis, e.g. 'Likely travels to Buenos Aires regularly'. Hold lightly — this is a conversation hypothesis, not a fact.",
    ),
  basis: z
    .string()
    .optional()
    .default("")
    .describe(
      "Signals supporting this hypothesis, e.g. 'Argentine nationality + Miami-based + career geography'.",
    ),
  confidence: z
    .string()
    .optional()
    .default("low")
    .describe(
      "One of: low, medium. Use `medium` only when multiple signals converge (e.g. nationality + location + career all point the same way).",
    ),
  type: z
    .string()
    .optional()
    .default("corridor")
    .describe(
      "One of: corridor (regular route), home_base (where they're from), cultural_root (ancestral/identity connection), upcoming (likely near-term trip based on signals).",
    ),
});

const CulturalIdentityHypothesisSchema = z.object({
  hypothesis: z
    .string()
    .describe(
      "Cultural CONNECTION POINTS — shared experiences that bond people. Example: 'World Cup is instant conversation fuel with other Argentines.' NOT religious identity, NOT political identity, NOT family structure assumptions from culture. The test: would this help two people connect at a dinner, or would it make someone uncomfortable?",
    ),
  basis: z
    .string()
    .optional()
    .default("")
    .describe("Nationality + visible signals (posts, bio, career geography)."),
  confidence: z
    .string()
    .optional()
    .default("low")
    .describe("One of: low, medium."),
});

const IntellectualInterestHypothesisSchema = z.object({
  hypothesis: z
    .string()
    .describe(
      "From posts, career arc, and sector: what they're probably reading or thinking about beyond their job title, e.g. 'Probably following AI governance debates closely'.",
    ),
  basis: z
    .string()
    .optional()
    .default("")
    .describe(
      "Signals supporting this, e.g. '3 LinkedIn posts about AI regulation in past month'.",
    ),
  confidence: z
    .string()
    .optional()
    .default("low")
    .describe("One of: low, medium."),
});

const NetworkHypothesisSchema = z.object({
  hypothesis: z
    .string()
    .describe(
      "Who they probably know / what communities they're embedded in, INCLUDING ecosystem provenance (Endeavor/YC/Stanford GSB/Techstars/Wharton angels) and professional tribes. This is network DNA, not a mission. Example: 'Product of the Endeavor network — likely shares vocabulary, mentors, and 10+ mutual connections with other Endeavor founders in the room.' Or: 'Part of the Miami fintech migration wave — arrived 2020-2022, embedded in the Brickell corridor scene.'",
    ),
  basis: z
    .string()
    .optional()
    .default("")
    .describe(
      "Ecosystem affiliations, accelerator history, alumni networks, geographic professional communities.",
    ),
  confidence: z
    .string()
    .optional()
    .default("low")
    .describe("One of: low, medium."),
});

const LifeStageSchema = z
  .object({
    estimatedAgeRange: z
      .string()
      .optional()
      .default("")
      .describe("e.g. '40-45'."),
    basis: z
      .string()
      .optional()
      .default("")
      .describe(
        "How you derived the age range, e.g. 'First company founded 2008, MBA 2005 — implies graduation ~2003, age ~43'.",
      ),
    stage: z
      .string()
      .optional()
      .default("building")
      .describe(
        "PROFESSIONAL life stage only — one of: early_career (0-8 years: building reputation, finding product-market fit, seeking mentorship), building (8-15 years: scaling operations, first leadership transitions, building teams across markets), scaling, established (15-25 years: board roles, portfolio building, ecosystem influence, succession questions), legacy (25+ years: institutional building, giving back, cross-generational mentorship).",
      ),
    professionalThemes: z
      .array(z.string())
      .optional()
      .default([])
      .describe(
        "Professional questions someone at this stage and scale typically navigates, e.g. 'capital strategy becomes a real job at this scale', 'operator-to-board transition', 'building across two geographies', 'identity beyond the company after 20 years'. NEVER: aging parents, kids, health, divorce, emotional states, relationship status.",
      ),
    confidence: z
      .string()
      .optional()
      .default("low")
      .describe("One of: low, medium."),
  })
  .nullable();

const LifeContextHypothesesSchema = z.object({
  travel: z
    .array(TravelHypothesisSchema)
    .optional()
    .default([])
    .describe("Travel corridors they likely fly regularly."),
  culturalIdentity: z
    .array(CulturalIdentityHypothesisSchema)
    .optional()
    .default([])
    .describe(
      "Cultural connection points — what stories might they tell about where they're from? What shared cultural moments (elections, sports events, economic crises) might be vivid for them?",
    ),
  intellectualInterests: z
    .array(IntellectualInterestHypothesisSchema)
    .optional()
    .default([])
    .describe(
      "Subjects they probably follow or engage with outside their job title.",
    ),
  networkHypotheses: z
    .array(NetworkHypothesisSchema)
    .optional()
    .default([])
    .describe(
      "Who they probably know / which communities they're embedded in, including ecosystem provenance.",
    ),
  lifeStage: LifeStageSchema.optional()
    .default(null)
    .describe(
      "Professional life stage inference. Set to null if career data is insufficient. Do NOT speculate about personal life.",
    ),
});

export const PersonCoreBriefSchema = z.object({
  situation: z
    .string()
    .describe(
      "2-3 sentences on what makes this person's current chapter interesting. If there's a genuine tension or choice they're weighing, name it — but don't manufacture one. Sometimes the interesting thing is a clear trajectory with momentum, not a contradiction. Read their career arc as a story. If personal data reveals a key identity dimension (sports owner, cultural figure, endurance athlete), weave it in naturally.",
    ),
  humanDimension: z
    .string()
    .describe(
      "1-3 sentences about who this person is BEYOND the spreadsheet. What do their LinkedIn posts, bio, career moves, or public interests reveal about how they think and what they value? If someone says they love futbol and owns three soccer clubs, that's not a footnote — it's a window into their decision-making DNA. If no personal data is available, write ONE short sentence acknowledging the gap — do NOT speculate at length about what the gap might mean.",
    ),
  reasonsToMeet: z
    .array(ReasonToMeetSchema)
    .describe(
      "10-20 specific reasons someone would want to meet this person. Each is a potential match. Distribution: max 5 learn/offering (cap prevents one dimension from dominating); at least 3 non-transactional (play, explore, like, mission); at least 2 with direction `seeking`; at least 1 explore/both naming a SPECIFIC cross-domain bridge; at least 1 reason should include a `keywords` array.",
    ),
  blindSpots: z
    .string()
    .optional()
    .default("")
    .describe(
      "1 sentence on a SPECIFIC role or archetype AT THIS KIND OF EVENT they'd walk past. Name a concrete archetype and explain the unexpected overlap. Example: 'Might walk past the customs broker turned SaaS founder — but they've solved the same trust-at-scale problem from the regulated side.' The test: if you could say it about 50% of the room, it's too generic.",
    ),
  experientialKnowledge: z
    .array(z.string())
    .optional()
    .default([])
    .describe(
      "1-2 areas of practical wisdom from their experience — vendor opinions, decision frameworks, hard-won operational knowledge, or lived-through transitions that others would benefit from. Be specific: 'has navigated enterprise AI procurement cycles' not 'knows about AI'. RARE and TRANSFERABLE.",
    ),
  hiddenExpertise: z
    .array(z.string())
    .optional()
    .default([])
    .describe(
      "0-2 capabilities from previous careers or non-obvious dimensions of their background that don't show on their current LinkedIn but create unexpected connection potential.",
    ),
  lifeMissions: z
    .array(LifeMissionSchema)
    .optional()
    .default([])
    .describe(
      "Causes the person is sustainedly committed to BEYOND their primary job. ONLY if there is DIRECT evidence. Return an empty array if no direct evidence exists.",
    ),
  careerSignature: CareerSignatureSchema.optional().default({
    pattern: "",
    transitions: [],
    whatCarries: "",
    whatDoesnt: "",
    currentChapter: "",
  }),
  lifeContextHypotheses: LifeContextHypothesesSchema.optional().default({
    travel: [],
    culturalIdentity: [],
    intellectualInterests: [],
    networkHypotheses: [],
    lifeStage: null,
  }),
  evidenceSources: z.array(z.string()).optional().default([]),
  facts: z
    .array(ClaimSchema)
    .optional()
    .default([])
    .describe(
      "Declared + observed claims about this person (v40 §2). Each is a structured envelope with provenance and shareability — the card writer voices each per its tier and shareability. Min coverage: 3 facts. Inferred claims belong on `hypotheses`, never here.",
    ),
  hypotheses: z
    .array(ClaimSchema)
    .optional()
    .default([])
    .describe(
      "Inferred claims — synthesis across evidence (v40 §2). Each carries `tier: 'inferred'` and a `confidence` level. Voice exploratorily downstream ('might be', 'could be') — never assert. Min coverage: 2 hypotheses.",
    ),
});

export type PersonCoreBrief = z.infer<typeof PersonCoreBriefSchema>;
